import { Analytics } from '@cobliteam/analytics';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import { useTranslation } from '@cobliteam/i18n';
import Alert from '@cobliteam/ui/components/Alert';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import Feedback from '@cobliteam/ui-business/components/Feedback';
import type { ChatStatus, LanguageModelUsage } from 'ai';
import { nanoid } from 'nanoid';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useAIAssistant } from '../store';
import type { CobliChatUIMessage, SendUserTextMessage } from '../types';
import ContextUsage from './elements/ContextUsage';
import { PromptInput, PromptInputSubmit, PromptInputTextarea } from './elements/PromptInput';
import WhatsAppPhoneNumberBanner from './WhatsAppPhoneNumberBanner';

interface AIChatPromptInputProps {
  placeholder?: string;
  loading?: boolean;
  className?: string;
  sendMessage: SendUserTextMessage;
  status: ChatStatus;
  stop: () => void;
  /** Analytics source identifier for tracking where events originate */
  source?: string;
  messages?: CobliChatUIMessage[];
}

type PromptMessageOptions = {
  messageText: string;
};

const MENTION_TRIGGER_PATTERN = /(^|\s)@\S+/;

type ContextUsageData = {
  usedTokens: number;
  maxTokens: number;
  usage?: LanguageModelUsage;
  modelId?: string;
};

type PromptComposerProps = {
  input: string;
  isFocused: boolean;
  isSendingDisabled: boolean;
  placeholder: string;
  status: ChatStatus;
  submitError: string | null;
  onBlur: () => void;
  onFocus: () => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  onValueChange: (value: string) => void;
};

const useAIChatPromptController = ({ placeholder, sendMessage, status, stop, source, messages }: Omit<AIChatPromptInputProps, 'className' | 'loading'>) => {
  const { t } = useTranslation('ai');
  const [input, setInput] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(true);
  const { createFakeThread, threadId, newThreadId } = useAIAssistant();

  const { environment } = useDashboard();
  const isDev = environment?.isDev;
  const resolvedPlaceholder = placeholder ?? t('prompt_placeholder');
  const isSendingDisabled = status === 'ready' && !input.trim();
  const activeThreadId = threadId || newThreadId;
  const { getUser } = useUser();
  const isCobler = getUser()?.email?.endsWith('@cobli.co') ?? false;

  const lastKnownUsageRef = useRef<ContextUsageData | null>(null);

  const contextUsage = useMemo((): ContextUsageData | null => {
    const lastAssistant = messages && [...messages].reverse().find((m) => m.role === 'assistant');
    const meta = lastAssistant?.metadata;
    const maxTokens = meta?.maxTokens ?? 200000;
    if (!meta?.inputTokens) return lastKnownUsageRef.current ?? null;
    const result: ContextUsageData = {
      usedTokens: meta.inputTokens,
      maxTokens,
      modelId: meta.modelId,
      usage: {
        inputTokens: meta.inputTokens,
        outputTokens: meta.outputTokens,
        outputTokenDetails: { textTokens: undefined, reasoningTokens: meta.reasoningTokens },
        inputTokenDetails: { noCacheTokens: undefined, cacheReadTokens: meta.cachedInputTokens, cacheWriteTokens: undefined },
        totalTokens: (meta.inputTokens ?? 0) + (meta.outputTokens ?? 0),
      },
    };
    lastKnownUsageRef.current = result;
    return result;
  }, [messages]);

  const ensureThreadExists = useCallback(async () => {
    if (threadId) return false;

    await createFakeThread({ threadId: activeThreadId });
    return true;
  }, [activeThreadId, createFakeThread, threadId]);

  const reportSubmitError = useCallback(
    (err: unknown) => {
      setSubmitError(t('send_failed'));
      if (isDev) {
        const message = err instanceof Error ? err.message : String(err);
        // eslint-disable-next-line no-console
        console.error('[AIChatPromptInput] sendMessage failed:', message, err);
      }
    },
    [isDev, t]
  );

  const sendPromptMessage = useCallback(
    async ({ messageText }: PromptMessageOptions) => {
      try {
        await ensureThreadExists();

        Analytics.track('AI - Chat - Message Sent', {
          prompt: messageText,
          promptLength: messageText.length,
          hasMentions: MENTION_TRIGGER_PATTERN.test(messageText),
          source,
          threadId: activeThreadId,
        });
        await sendMessage({ role: 'user', id: nanoid(), parts: [{ type: 'text', text: messageText }] });
        return true;
      } catch (err) {
        reportSubmitError(err);
        return false;
      }
    },
    [activeThreadId, ensureThreadExists, reportSubmitError, sendMessage, source]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitError(null);

      if (status !== 'ready') {
        Analytics.track('AI - Chat - Generation Stopped', { source });
        stop();
        return;
      }

      const trimmedInput = input.trim();

      if (!trimmedInput) {
        Alert.error(t('please_type_message'));
        return;
      }

      const sent = await sendPromptMessage({ messageText: trimmedInput });
      if (sent) {
        setInput('');
      }
    },
    [input, sendPromptMessage, source, status, stop, t]
  );

  const handleValueChange = useCallback((value: string) => {
    setInput(value);
    setSubmitError(null);
  }, []);

  return {
    input,
    isFocused,
    isSendingDisabled,
    resolvedPlaceholder,
    status,
    submitError,
    contextUsage: isCobler ? contextUsage : null,
    handleBlur: () => setIsFocused(false),
    handleFocus: () => setIsFocused(true),
    handleSubmit,
    handleValueChange,
  };
};

const PromptComposer = ({ input, isFocused, isSendingDisabled, placeholder, status, submitError, onBlur, onFocus, onSubmit, onValueChange }: PromptComposerProps) => {
  return (
    <div className='relative flex flex-col'>
      <WhatsAppPhoneNumberBanner />
      <div className={cn('relative flex items-center rounded-xl transition-shadow', isFocused && 'ring-2 ring-primary-7')} onFocus={onFocus} onBlur={onBlur}>
        <PromptInput onSubmit={onSubmit} className='relative w-full border-gray-6 bg-white shadow-sm'>
          <PromptInputTextarea
            autoFocus
            disabled={status !== 'ready'}
            maxHeight={176}
            minHeight={68}
            onChange={(event) => onValueChange(event.target.value)}
            placeholder={placeholder}
            value={input}
            className='px-4 pt-3.5 pr-14 pb-11'
          />
          <PromptInputSubmit disabled={isSendingDisabled} status={status} className='absolute right-3 bottom-3 z-10 size-9 rounded-lg' />
        </PromptInput>
      </div>
      {submitError && (
        <span className='text-danger-11 text-xs' role='alert'>
          {submitError}
        </span>
      )}
    </div>
  );
};

type PromptFooterProps = {
  contextUsage: ContextUsageData | null;
};

const PromptFooter = ({ contextUsage }: PromptFooterProps) => {
  const { t } = useTranslation('ai');

  return (
    <div className='flex h-8 items-center justify-between gap-3 overflow-hidden'>
      <Feedback source='ai-chat' showTooltip={false}>
        <Typography
          variant='text-sm'
          color='secondary'
          className='flex shrink-0 cursor-pointer flex-nowrap items-center gap-1 whitespace-nowrap text-gray-9 [&>svg]:shrink-0'
          onClick={() => Analytics.track('AI - Chat - Feedback Button Clicked')}
        >
          <Icon name='megaphone' className='text-gray-9' />
          {t('send_feedback')}
        </Typography>
      </Feedback>
      {contextUsage && <ContextUsage maxTokens={contextUsage.maxTokens} modelId={contextUsage.modelId} usage={contextUsage.usage} usedTokens={contextUsage.usedTokens} />}
      <div className='flex min-w-0 flex-1 items-center justify-end gap-1 truncate whitespace-nowrap text-gray-9 text-xs italic leading-4'>{t('disclaimer')}</div>
    </div>
  );
};

const AIChatPromptInput = ({ placeholder, loading = false, className, sendMessage, status, stop, source = 'sidepanel', messages }: AIChatPromptInputProps) => {
  const prompt = useAIChatPromptController({ placeholder, sendMessage, status, stop, source, messages });

  if (loading) return null;

  return (
    <div data-testid='ai-chat-input' className={cn('flex w-full shrink-0 flex-col gap-1 px-4', className)}>
      <PromptComposer
        input={prompt.input}
        isFocused={prompt.isFocused}
        isSendingDisabled={prompt.isSendingDisabled}
        placeholder={prompt.resolvedPlaceholder}
        status={prompt.status}
        submitError={prompt.submitError}
        onBlur={prompt.handleBlur}
        onFocus={prompt.handleFocus}
        onSubmit={prompt.handleSubmit}
        onValueChange={prompt.handleValueChange}
      />
      <PromptFooter contextUsage={prompt.contextUsage} />
    </div>
  );
};

export default memo(AIChatPromptInput);
